import { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft,
  Upload,
  Camera,
  X,
  AlertCircle,
  ArrowRight,
  Sparkles,
  RotateCcw,
  Check,
  Heart,
  RefreshCw,
} from 'lucide-react';
import { useDraftDreamStore } from '../lib/store';

const MAX_PHOTOS = 6;
const MIN_PHOTOS = 1;

type Step = 1 | 2 | 3;
type PhotoMode = 'choice' | 'camera' | 'upload';

const STEP_LABELS: Record<Step, string> = {
  1: 'Chargez vos photos',
  2: 'Décrivez votre rêve',
  3: 'Finalisez votre création',
};

const POSES = [
  { key: 'face_smile', label: 'Visage souriant', desc: 'Regardez la caméra avec un sourire naturel' },
  { key: 'face_neutral', label: 'Visage neutre', desc: 'Regardez la caméra, expression naturelle' },
  { key: 'profile_smile', label: 'Profil souriant', desc: 'Tournez la tête de côté avec un sourire' },
  { key: 'profile_neutral', label: 'Profil neutre', desc: 'Tournez la tête de côté, expression neutre' },
];

// ─── Camera capture (guided poses) ───────────
function CameraCapture({
  onPhotosComplete,
  onSwitchToUpload,
}: {
  onPhotosComplete: (files: File[]) => void;
  onSwitchToUpload: () => void;
}) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [currentPoseIndex, setCurrentPoseIndex] = useState(0);
  const [capturedPhotos, setCapturedPhotos] = useState<(File | null)[]>([null, null, null, null]);
  const [isCapturing, setIsCapturing] = useState(false);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');

  const currentPose = POSES[currentPoseIndex];
  const completedCount = capturedPhotos.filter(Boolean).length;

  const initCamera = useCallback(async () => {
    setIsLoading(true);
    setErrorMessage(null);
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode, width: { ideal: 1280 }, height: { ideal: 720 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        if (videoRef.current.readyState < 1) {
          await new Promise<void>((resolve) => {
            const timeout = setTimeout(() => resolve(), 3000);
            videoRef.current!.addEventListener('loadedmetadata', () => { clearTimeout(timeout); resolve(); }, { once: true });
          });
        }
        try { await videoRef.current.play(); } catch {}
      }
      setHasPermission(true);
      setIsLoading(false);
    } catch (err: any) {
      setIsLoading(false);
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        setErrorMessage('Accès à la caméra refusé. Vérifiez les permissions de votre navigateur.');
      } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
        setErrorMessage('Aucune caméra détectée sur cet appareil.');
      } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
        setErrorMessage('La caméra est utilisée par une autre application.');
      } else {
        setErrorMessage('Impossible d\'accéder à la caméra.');
      }
      setHasPermission(false);
    }
  }, [facingMode]);

  useEffect(() => {
    initCamera();
    return () => { streamRef.current?.getTracks().forEach((t) => t.stop()); };
  }, [initCamera]);

  const capturePhoto = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || isCapturing) return;
    setIsCapturing(true);
    for (let i = 3; i > 0; i--) {
      setCountdown(i);
      await new Promise((r) => setTimeout(r, 1000));
    }
    setCountdown(null);
    const canvas = canvasRef.current;
    const video = videoRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      if (facingMode === 'user') { ctx.translate(canvas.width, 0); ctx.scale(-1, 1); }
      ctx.drawImage(video, 0, 0);
    }
    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], `pose_${currentPose.key}_${Date.now()}.jpg`, { type: 'image/jpeg' });
        const newPhotos = [...capturedPhotos];
        newPhotos[currentPoseIndex] = file;
        setCapturedPhotos(newPhotos);
        if (currentPoseIndex < POSES.length - 1) {
          setTimeout(() => setCurrentPoseIndex(currentPoseIndex + 1), 500);
        }
      }
      setIsCapturing(false);
    }, 'image/jpeg', 0.9);
  }, [currentPoseIndex, capturedPhotos, isCapturing, facingMode, currentPose.key]);

  const retakePhoto = useCallback(() => {
    const newPhotos = [...capturedPhotos];
    newPhotos[currentPoseIndex] = null;
    setCapturedPhotos(newPhotos);
  }, [currentPoseIndex, capturedPhotos]);

  const handleComplete = useCallback(() => {
    const validPhotos = capturedPhotos.filter((p): p is File => p !== null);
    if (validPhotos.length >= 1) onPhotosComplete(validPhotos);
  }, [capturedPhotos, onPhotosComplete]);

  if (hasPermission === false) {
    return (
      <div className="text-center py-10 space-y-5">
        <div className="w-16 h-16 rounded-2xl bg-white/5 flex items-center justify-center mx-auto">
          <AlertCircle className="w-8 h-8 text-white/30" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white/80 mb-2">Caméra indisponible</h3>
          <p className="text-white/45 text-sm max-w-xs mx-auto leading-relaxed">{errorMessage}</p>
        </div>
        <div className="flex gap-3 justify-center">
          <button onClick={initCamera} className="glass-card px-5 py-2.5 text-white/60 flex items-center gap-2 text-sm">
            <RefreshCw className="w-4 h-4" /> Réessayer
          </button>
          <button onClick={onSwitchToUpload} className="btn-primary px-5 py-2.5 flex items-center gap-2 text-sm">
            <Upload className="w-4 h-4" /> Importer
          </button>
        </div>
        <video ref={videoRef} className="hidden" />
        <canvas ref={canvasRef} className="hidden" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Pose instruction */}
      {!isLoading && (
        <motion.div key={currentPoseIndex} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <h3 className="text-base font-semibold text-white/90">{currentPose.label}</h3>
          <p className="text-white/45 text-sm">{currentPose.desc}</p>
        </motion.div>
      )}

      {/* Camera — full width */}
      <div className="relative w-full rounded-2xl overflow-hidden bg-black" style={{ aspectRatio: '4/3' }}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className={`w-full h-full object-cover ${facingMode === 'user' ? 'scale-x-[-1]' : ''}`}
        />
        <canvas ref={canvasRef} className="hidden" />

        {isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black">
            <div className="animate-spin w-8 h-8 border-4 border-white/10 border-t-white/50 rounded-full mb-3" />
            <p className="text-white/40 text-sm">Initialisation...</p>
          </div>
        )}

        {/* Face guide */}
        <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
          <div className={`w-32 h-40 border-2 border-dashed rounded-full opacity-25 ${
            currentPose.key.includes('profile') ? 'border-white/80' : 'border-white'
          }`} />
        </div>

        {/* Countdown overlay */}
        <AnimatePresence>
          {countdown !== null && (
            <motion.div
              initial={{ scale: 0.5, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1.5, opacity: 0 }}
              className="absolute inset-0 flex items-center justify-center bg-black/30"
            >
              <span className="text-6xl font-bold text-white drop-shadow-lg">{countdown}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Switch camera */}
        <button
          onClick={() => setFacingMode((p) => (p === 'user' ? 'environment' : 'user'))}
          className="absolute top-3 right-3 p-2.5 rounded-full"
          style={{ background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(8px)' }}
        >
          <RotateCcw className="w-4 h-4 text-white/80" />
        </button>

        {/* Captured indicator */}
        {capturedPhotos[currentPoseIndex] && (
          <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-emerald-500/90 text-white text-xs font-semibold flex items-center gap-1">
            <Check className="w-3 h-3" /> OK
          </div>
        )}

        {/* Pose dots — inside camera */}
        {!isLoading && (
          <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-2">
            {POSES.map((pose, index) => (
              <button
                key={pose.key}
                onClick={() => setCurrentPoseIndex(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentPoseIndex
                    ? 'bg-white scale-125'
                    : capturedPhotos[index]
                      ? 'bg-white/60'
                      : 'bg-white/20'
                }`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Controls below camera */}
      {!isLoading && (
        <div className="space-y-3">
          {/* Capture / retake / next */}
          {capturedPhotos[currentPoseIndex] ? (
            <div className="flex gap-3">
              <button onClick={retakePhoto} className="glass-card flex-1 py-3 text-sm text-white/60 flex items-center justify-center gap-2">
                <RotateCcw className="w-4 h-4" /> Reprendre
              </button>
              {currentPoseIndex < POSES.length - 1 ? (
                <button
                  onClick={() => setCurrentPoseIndex(currentPoseIndex + 1)}
                  className="btn-primary flex-1 py-3 text-sm flex items-center justify-center gap-2"
                >
                  Suivant <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  onClick={handleComplete}
                  disabled={completedCount < 1}
                  className="btn-primary flex-1 py-3 text-sm flex items-center justify-center gap-2 disabled:opacity-40"
                >
                  <Check className="w-4 h-4" /> Terminer
                </button>
              )}
            </div>
          ) : (
            <button
              onClick={capturePhoto}
              disabled={isCapturing}
              className="btn-primary w-full py-4 text-sm font-semibold flex items-center justify-center gap-2"
            >
              <Camera className="w-5 h-5" />
              {isCapturing ? 'Capture en cours...' : 'Prendre la photo'}
            </button>
          )}

          {/* Thumbnails */}
          <div className="flex justify-center gap-2.5">
            {capturedPhotos.map((photo, index) => (
              <button
                key={index}
                onClick={() => setCurrentPoseIndex(index)}
                className={`w-12 h-12 rounded-xl overflow-hidden border-2 transition-all ${
                  index === currentPoseIndex
                    ? 'border-white/60 ring-2 ring-white/15'
                    : photo
                      ? 'border-white/25'
                      : 'border-white/8'
                }`}
              >
                {photo ? (
                  <img src={URL.createObjectURL(photo)} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-white/5 flex items-center justify-center">
                    <span className="text-white/15 text-[10px] font-medium">{index + 1}</span>
                  </div>
                )}
              </button>
            ))}
          </div>

          {/* Switch to upload */}
          <div className="text-center">
            <button onClick={onSwitchToUpload} className="text-white/35 hover:text-white/60 text-xs inline-flex items-center gap-1.5 transition-colors">
              <Upload className="w-3 h-3" /> Importer des fichiers à la place
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main page ───────────────────────────────
export default function CreateDreamPage() {
  const navigate = useNavigate();
  const { photos, dreamText, rejectText, addPhoto, removePhoto, setDreamText, setRejectText } =
    useDraftDreamStore();

  const [step, setStep] = useState<Step>(1);
  const [photoMode, setPhotoMode] = useState<PhotoMode>('choice');
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const remaining = MAX_PHOTOS - photos.length;
    if (remaining <= 0) { setError(`Maximum ${MAX_PHOTOS} photos.`); return; }
    setError('');
    Array.from(files).slice(0, remaining).forEach((f) => addPhoto(f));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCameraComplete = (files: File[]) => {
    files.forEach((f) => addPhoto(f));
    if (photos.length + files.length >= MIN_PHOTOS) {
      setStep(2);
    } else {
      setPhotoMode('upload');
    }
  };

  const handleBack = () => {
    if (step === 1 && photoMode !== 'choice') { setPhotoMode('choice'); }
    else if (step === 1) { navigate('/'); }
    else { setStep((s) => (s - 1) as Step); }
    setError('');
  };

  const handleNext = () => {
    if (step === 1) {
      if (photos.length === 0) { setError('Ajoutez au moins une photo'); return; }
      setStep(2);
    } else if (step === 2) {
      if (dreamText.trim().length < 20) { setError('Décrivez votre rêve (min. 20 caractères)'); return; }
      setStep(3);
    }
    setError('');
  };

  const slideVariants = {
    enter: { opacity: 0, x: 40 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -40 },
  };

  return (
    <div className="min-h-screen flex flex-col safe-area-inset">
      <div className="fixed inset-0 bg-system-carbon-500 -z-10" />

      {/* Header */}
      <header className="px-5 pt-4 pb-3">
        <div className="flex items-center gap-3">
          <button onClick={handleBack} className="w-9 h-9 rounded-xl flex items-center justify-center text-white/40 hover:text-white/70 hover:bg-white/5 transition-all">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <p className="text-[13px] font-semibold text-white/90">
              <span className="text-white/40">{step}/3</span>
              <span className="mx-2 text-white/15">—</span>
              {STEP_LABELS[step]}
            </p>
            {/* Progress bar */}
            <div className="mt-2 h-[3px] rounded-full bg-white/8 overflow-hidden">
              <motion.div
                className="h-full rounded-full bg-white/40"
                animate={{ width: `${(step / 3) * 100}%` }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
              />
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 px-5 pb-8 overflow-y-auto">
        <AnimatePresence mode="wait">
          {/* ── Step 1: Photos ── */}
          {step === 1 && (
            <motion.div
              key={`step1-${photoMode}`}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25 }}
              className="space-y-5 pt-3"
            >
              {photoMode === 'choice' && (
                <>
                  <p className="text-white/40 text-sm leading-relaxed">
                    Ajoutez jusqu'à {MAX_PHOTOS} photos de vous pour personnaliser vos images d'attraction.
                  </p>

                  {/* Webcam recommendation */}
                  <div
                    className="p-4 rounded-2xl flex items-start gap-3"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}
                  >
                    <div className="w-9 h-9 rounded-xl bg-white/8 flex items-center justify-center shrink-0">
                      <Camera className="w-4.5 h-4.5 text-white/50" />
                    </div>
                    <div>
                      <p className="text-[13px] font-medium text-white/70">Webcam recommandée</p>
                      <p className="text-xs text-white/35 mt-1 leading-relaxed">
                        Nous vous guiderons à travers 4 poses pour un rendu optimal de vos visualisations.
                      </p>
                    </div>
                  </div>

                  {/* Choice cards */}
                  <div className="grid grid-cols-2 gap-3">
                    <button
                      onClick={() => setPhotoMode('camera')}
                      className="relative rounded-2xl p-5 text-left transition-all active:scale-[0.98]"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}
                    >
                      <span
                        className="absolute top-2.5 right-2.5 px-2 py-0.5 rounded-full text-[9px] font-bold tracking-wide uppercase"
                        style={{ background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
                      >
                        Reco
                      </span>
                      <div className="w-11 h-11 rounded-xl bg-white/8 flex items-center justify-center mb-3">
                        <Camera className="w-5 h-5 text-white/60" />
                      </div>
                      <h3 className="text-[13px] font-semibold text-white/80 mb-0.5">Prendre des photos</h3>
                      <p className="text-[11px] text-white/30 leading-relaxed">
                        4 poses guidées avec votre caméra
                      </p>
                    </button>

                    <button
                      onClick={() => setPhotoMode('upload')}
                      className="rounded-2xl p-5 text-left transition-all active:scale-[0.98]"
                      style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
                    >
                      <div className="w-11 h-11 rounded-xl bg-white/8 flex items-center justify-center mb-3">
                        <Upload className="w-5 h-5 text-white/60" />
                      </div>
                      <h3 className="text-[13px] font-semibold text-white/80 mb-0.5">Importer</h3>
                      <p className="text-[11px] text-white/30 leading-relaxed">
                        Depuis votre galerie
                      </p>
                    </button>
                  </div>

                  {photos.length > 0 && (
                    <button
                      onClick={handleNext}
                      className="btn-primary w-full py-3.5 text-sm font-semibold flex items-center justify-center gap-2"
                    >
                      Continuer avec {photos.length} photo{photos.length > 1 ? 's' : ''}
                      <ArrowRight className="w-4 h-4" />
                    </button>
                  )}
                </>
              )}

              {photoMode === 'camera' && (
                <CameraCapture
                  onPhotosComplete={handleCameraComplete}
                  onSwitchToUpload={() => setPhotoMode('upload')}
                />
              )}

              {photoMode === 'upload' && (
                <div className="space-y-5">
                  <p className="text-white/40 text-sm">
                    Sélectionnez de {MIN_PHOTOS} à {MAX_PHOTOS} photos de vous
                  </p>

                  <div className="grid grid-cols-3 gap-3">
                    {photos.map((photo, i) => (
                      <div key={i} className="relative aspect-square">
                        <img src={photo.preview} alt="" className="w-full h-full object-cover rounded-xl" />
                        <button
                          onClick={() => removePhoto(i)}
                          className="absolute top-1.5 right-1.5 w-6 h-6 rounded-full flex items-center justify-center"
                          style={{ background: 'rgba(0,0,0,0.6)' }}
                        >
                          <X className="w-3.5 h-3.5 text-white" />
                        </button>
                      </div>
                    ))}
                    {photos.length < MAX_PHOTOS && (
                      <button
                        onClick={() => fileInputRef.current?.click()}
                        className="aspect-square rounded-xl flex flex-col items-center justify-center gap-1.5 transition-colors"
                        style={{ border: '2px dashed rgba(255,255,255,0.1)' }}
                      >
                        <Upload className="w-5 h-5 text-white/20" />
                        <span className="text-[10px] text-white/20">Ajouter</span>
                      </button>
                    )}
                  </div>

                  <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={handleFileSelect} className="hidden" />

                  <p className="text-center text-xs text-white/20">{photos.length} / {MAX_PHOTOS}</p>

                  {error && (
                    <div className="flex items-center gap-2 text-red-400 text-sm justify-center">
                      <AlertCircle className="w-4 h-4" /> <span>{error}</span>
                    </div>
                  )}

                  {photos.length >= MIN_PHOTOS && (
                    <button onClick={handleNext} className="btn-primary w-full py-4 font-semibold flex items-center justify-center gap-2">
                      Continuer <ArrowRight className="w-4 h-4" />
                    </button>
                  )}

                  <div className="text-center">
                    <button onClick={() => setPhotoMode('camera')} className="text-white/30 hover:text-white/60 text-xs inline-flex items-center gap-1.5 transition-colors">
                      <Camera className="w-3 h-3" /> Utiliser la caméra
                    </button>
                  </div>
                </div>
              )}
            </motion.div>
          )}

          {/* ── Step 2: Dream description ── */}
          {step === 2 && (
            <motion.div
              key="step2"
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25 }}
              className="space-y-5 pt-3"
            >
              <p className="text-white/40 text-sm leading-relaxed">
                Décrivez la vie que vous souhaitez manifester. Plus votre description est détaillée, plus vos images seront personnalisées.
              </p>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-[13px] font-medium text-white/60">
                  <Heart className="w-4 h-4 text-white/30" /> Mon rêve
                </label>
                <textarea
                  value={dreamText}
                  onChange={(e) => setDreamText(e.target.value)}
                  placeholder="Je vis dans une maison lumineuse au bord de la mer, entourée de nature et de sérénité..."
                  className="input-glass min-h-[160px] resize-none text-[15px] leading-relaxed"
                  maxLength={2000}
                />
                <div className="flex justify-between text-[11px]">
                  <span className={dreamText.length < 20 ? 'text-white/35' : 'text-white/15'}>
                    Min. 20 caractères
                  </span>
                  <span className="text-white/15">{dreamText.length}/2000</span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[13px] font-medium text-white/60">Éléments à éviter <span className="text-white/25 font-normal">(optionnel)</span></label>
                <textarea
                  value={rejectText}
                  onChange={(e) => setRejectText(e.target.value)}
                  placeholder="Pas de pluie, pas de foule, pas de bureau..."
                  className="input-glass min-h-[80px] resize-none text-[15px] leading-relaxed"
                  maxLength={1000}
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-red-400 text-sm justify-center">
                  <AlertCircle className="w-4 h-4" /> <span>{error}</span>
                </div>
              )}

              <button
                onClick={handleNext}
                disabled={dreamText.trim().length < 20}
                className="btn-primary w-full py-4 font-semibold disabled:opacity-30 flex items-center justify-center gap-2"
              >
                Continuer <ArrowRight className="w-4 h-4" />
              </button>
            </motion.div>
          )}

          {/* ── Step 3: Signup prompt ── */}
          {step === 3 && (
            <motion.div
              key="step3"
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.25 }}
              className="flex-1 flex flex-col items-center justify-center min-h-[65vh] text-center"
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ delay: 0.1, duration: 0.4 }}
                className="space-y-8"
              >
                <div className="w-20 h-20 rounded-3xl bg-white/6 flex items-center justify-center mx-auto">
                  <Sparkles className="w-9 h-9 text-white/50" />
                </div>

                <div className="space-y-3 max-w-[280px] mx-auto">
                  <h2 className="text-2xl font-display font-bold text-white leading-tight">
                    Vous y êtes presque !
                  </h2>
                  <p className="text-white/50 text-[15px] leading-relaxed">
                    Vous n'avez plus qu'à vous inscrire pour recevoir vos photos d'attraction personnalisées !
                  </p>
                </div>

                <div className="space-y-4 w-full max-w-[280px] mx-auto">
                  <div className="flex items-center justify-center gap-3 text-xs text-white/25">
                    <span className="flex items-center gap-1"><Check className="w-3 h-3" /> {photos.length} photo{photos.length > 1 ? 's' : ''}</span>
                    <span className="w-1 h-1 rounded-full bg-white/15" />
                    <span className="flex items-center gap-1"><Check className="w-3 h-3" /> Rêve défini</span>
                  </div>
                  <button
                    onClick={() => navigate('/access')}
                    className="btn-primary w-full py-4 font-semibold flex items-center justify-center gap-2 text-[15px]"
                  >
                    S'inscrire <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}
